import React from 'react';
import axios from 'axios';
import { API_BASE_URL } from '@config';
import * as ActionsDialog from 'app/store/actions';
import { showMessage } from 'app/store/actions/fuse';
import { Button, DialogActions, DialogContent, DialogContentText, DialogTitle, Icon, Typography } from '@material-ui/core';
import Logs from '../../Logs';
import StackErrorDialog from '../../StackErrorDialog';

// SET
export const SET_STACKS_LIST = 'SET_STACKS_LIST';
export const setStacksList = stacks => ({
	type: SET_STACKS_LIST,
	payload: stacks
});

export const SET_STACK = 'SET_STACK';
export const setStack = stack => ({
	type: SET_STACK,
	payload: stack
});

export const SET_OS_TYPES = 'SET_OS_TYPES';
export const setProviderOsTypes = os_types => ({
	type: SET_OS_TYPES,
	payload: os_types
});

export const SET_IMAGES_INFO = 'SET_IMAGES_INFO';
export const setImagesInfo = images_info => ({
	type: SET_IMAGES_INFO,
	payload: images_info
});

export const SET_ACM_IMAGES = 'SET_ACM_IMAGES';
export const setAcmImages = images => ({
	type: SET_ACM_IMAGES,
	payload: images
});

export const SET_ACM_SHAPES = 'SET_ACM_SHAPES';
export const setAcmShapes = images => ({
	type: SET_ACM_SHAPES,
	payload: images
});

export const SET_SELECTED = 'SET_SELECTED';
export const setSelected = stack => ({
	type: SET_SELECTED,
	payload: stack
});

// image-customization
export const SET_CUSTOMIZATION = 'SET_CUSTOMIZATION';
export const setCustomization = session => ({
	type: SET_CUSTOMIZATION,
	payload: session
});

export const SET_VERSIONS = 'SET_VERSIONS';
export const setVersions = versions => ({
	type: SET_VERSIONS,
	payload: versions
});

export const SET_SHAPES = 'SET_SHAPES';
export const setShapes = shapes => ({
	type: SET_SHAPES,
	payload: shapes
});

export const SET_SEARCH_TEXT = 'SET_SEARCH_TEXT';
export const setSearchText = event => ({
	type: SET_SEARCH_TEXT,
	searchText: event.target.value
});

export const SET_STACK_ERROR = 'SET_STACK_ERROR';
export const setStackError = error => ({
	type: SET_STACK_ERROR,
	payload: error
});

export const SET_LOGS = 'SET_LOGS';
export const setLogs = logs => ({
	type: SET_LOGS,
	payload: logs
});
// CLEAR
export const CLEAR_ERROR = 'CLEAR_ERROR';
export const clearError = () => ({
	type: CLEAR_ERROR
});

export const CLEAR_STACK = 'CLEAR_STACK';
export const clearStack = () => ({
	type: CLEAR_STACK
});

export const CLEAR_STACKS_REDUCER = 'CLEAR_STACKS_REDUCER';
export const clearStacksReducer = () => ({
	type: CLEAR_STACKS_REDUCER
});

export const CLEAR_LOGS = 'CLEAR_LOGS';
export const clearLogs = () => ({
	type: CLEAR_LOGS
});

// * ////////////// ERRORS ////////////// * //

/**
 * Build the payload for setStackError from a rejected request.
 *
 * Two different shapes arrive here. The global axios interceptor in jwtService
 * rejects with the RESPONSE object, so for any request that got an HTTP error
 * response `err` IS the response and the body is `err.data`. When there is no
 * response at all (network failure, CORS, timeout) that interceptor re-rejects
 * the original axios error, whose body would be at `err.response.data`. Reading
 * only one of the two loses the server's message for half the failures, which
 * is why some call sites showed a generic string instead of the real reason.
 *
 * The whole body is spread through because consumers read `field` and
 * `stackName` as well as `message`.
 */
const stackError = (err, type) => {
	const body = (err && (err.data !== undefined ? err.data : err.response && err.response.data)) || {};
	const data = typeof body === 'string' ? { message: body } : body;
	return {
		...data,
		message: data.message || data.error || (err && err.message) || 'Something went wrong',
		type
	};
};

// Message-only variant, for the snackbar call sites.
const errMessage = (err, fallback) => stackError(err).message || fallback;

// * ////////////// HEADERS ////////////// * //
const headers = {
	headers: {
		admin: true
	}
};
const repeatHeader = {
	headers: {
		repeatCall: true
	}
};

// * ////////////// NEW ////////////// * //

export const newStack = () => dispatch => {
	const data = {
		builder: 'market',
		credential_id: '',
		buildMode: 'unified',
		data: {
			manager: 'torque',
			customize_image: false,
			disk_size: '',
			instance_id: '',
			isPrivate: false,
			minimal: false,
			prefix: 'core',
			proxy: '',
			region_id: '',
			resource_group_name: '',
			security_group_id: '',
			service_account: false,
			source_image: '',
			source_image_offer: '',
			source_image_publisher: '',
			source_image_sku: '',
			source_os_type: '',
			source_resource_group_name: '',
			ssh_username: '',
			subnet_id: '',
			subnet_network_id: '',
			type: '',
			volume_size: '',
			zone_id: '',
			features: [],
			mem: null,
			ocpus: null,
			provision: {
				nodeProvisioner: '',
				serverProvisioner: '',
				unifiedProvisioner: '',
				dataFiles: [],
				dataFilesB: []
			},
			rhel: {
				activation_key: null,
				organization_id: null,
			},
			vpc_id: '',
		},
		description: '',
		name: '',
		displayName: '',
		provider: ''
	};

	dispatch(setStack(data));
};

// * ////////////// REPEATS ////////////// * //

// These run on a timer in the background. A transient failure must not raise a
// stack error: the next tick retries anyway, and pushing one into the store
// makes Stack.js mark the open edit form invalid and disable Save (it treats a
// modal-type error as a form error on AWS/MAAS) for a request the user never
// made. Log and let the next poll recover.
export const getStacksRepeat = () => dispatch => {
	const request = axios.get(`${API_BASE_URL}/api/stacks/`, repeatHeader);
	return request
		.then(res => dispatch(setStacksList(res.data)))
		.catch(err => console.warn('[stacks] background refresh failed:', errMessage(err, 'request failed')));
};

export const getLogsRepeat = params => dispatch => {
	const request = axios.get(`${API_BASE_URL}/api/stacks/logs/${params.id}/${params.type}`, repeatHeader);
	return request
		.then(res => dispatch(setLogs(res.data.logs)))
		.catch(err => console.warn('[stacks] background log refresh failed:', errMessage(err, 'request failed')));
};

// * ////////////// GET ////////////// * //

export const getStacks = () => dispatch => {
	dispatch(ActionsDialog.toggleLoading());
	const request = axios.get(`${API_BASE_URL}/api/stacks/`);
	return request
		.then(response => dispatch(setStacksList(response.data)))
		.then(() => dispatch(ActionsDialog.toggleLoading()))
		.catch(err => {
			dispatch(ActionsDialog.toggleLoading());
			dispatch(setStackError(stackError(err, 'modal')));
		});
};

export const getStack = data => dispatch => {
	dispatch(ActionsDialog.toggleLoading());
	const request = axios.get(`${API_BASE_URL}/api/stacks/${data.id}`);
	return request
		.then(response => {
			dispatch(setStack(response.data));
			dispatch(ActionsDialog.toggleLoading());
		})
		.catch(err => {
			dispatch(setStackError(stackError(err, 'modal')));
			dispatch(ActionsDialog.toggleLoading());
		});
};
export const provider_os_types = (provider) => dispatch => {
	dispatch(ActionsDialog.toggleLoading());
	const request = axios.get(`${API_BASE_URL}/api/stacks/provider_os_type/${provider}`);
	return request
		.then(response => dispatch(setProviderOsTypes(response.data)))
		.then(() => dispatch(ActionsDialog.toggleLoading()))
		.catch(err => {
			dispatch(ActionsDialog.toggleLoading());
			dispatch(setStackError(stackError(err, 'modal')));
		});
};
export const images_info = (provider) => dispatch => {
	dispatch(ActionsDialog.toggleLoading());
	const request = axios.get(`${API_BASE_URL}/api/stacks/images_info/${provider}`);
	return request
		.then(response => dispatch(setImagesInfo(response.data)))
		.then(() => dispatch(ActionsDialog.toggleLoading()))
		.catch(err => {
			dispatch(ActionsDialog.toggleLoading());
			dispatch(setStackError(stackError(err, 'modal')));
		});
};

export const getLogs = params => dispatch => {
	dispatch(ActionsDialog.toggleLoading());
	const request = axios.get(`${API_BASE_URL}/api/stacks/logs/${params.id}/${params.type}`);
	return request
		.then(response => {
			dispatch(ActionsDialog.toggleLoading());
			dispatch(setLogs(response.data.logs));
		})
		.catch(err => {
			dispatch(ActionsDialog.toggleLoading());
			dispatch(setStackError(stackError(err, 'modal')));
		});
};

// get ACM images 
export const getAcmImages = () => dispatch => {
	dispatch(ActionsDialog.toggleLoading());
	const request = axios.get(`${API_BASE_URL}/api/stacks/acm_images`);
	return request
		.then(response => {
			dispatch(ActionsDialog.toggleLoading());
			dispatch(setAcmImages(response.data));
		})
		.catch(err => {
			dispatch(ActionsDialog.toggleLoading());
			dispatch(setStackError(stackError(err, 'modal')));
		});
}


// get ACM shapes 
export const getAcmShapes = () => dispatch => {
	dispatch(ActionsDialog.toggleLoading());
	const request = axios.get(`${API_BASE_URL}/api/stacks/acm_shapes`);
	return request
		.then(response => {
			dispatch(ActionsDialog.toggleLoading());
			dispatch(setAcmShapes(response.data));
		})
		.catch(err => {
			dispatch(ActionsDialog.toggleLoading());
			dispatch(setStackError(stackError(err, 'modal')));
		});
}

// * ////////////// POST ////////////// * //

export const saveStack = stack => dispatch => {
	const formData = new FormData();

	//add modeProvision file
	if (stack.data.provision.nodeProvisioner) {
		formData.append('nodeProvisioner', stack.data.provision.nodeProvisioner, stack.data.provision.nodeProvisioner.name);
	}

	if (stack.data.provision.serverProvisioner) {
		formData.append(
			'serverProvisioner',
			stack.data.provision.serverProvisioner,
			stack.data.provision.serverProvisioner.name
		);
	}

	if (stack.data.provision.unifiedProvisioner) {
		formData.append(
			'unifiedProvisioner',
			stack.data.provision.unifiedProvisioner,
			stack.data.provision.unifiedProvisioner.name
		);
	}

	if (stack.data.provision.dataFilesB) {
		stack.data.provision.dataFilesB.forEach(file => {
			formData.append(file.name, file);
			stack.data.provision.dataFiles = stack.data.provision.dataFiles.filter(e => e !== file.name);
		});
	}

	delete stack.data.provision.dataFilesB;
	formData.set('stack', JSON.stringify(stack).replace('{}', '""'));

	dispatch(ActionsDialog.toggleLoading());
	const request = axios.post(`${API_BASE_URL}/api/stacks/`, formData, headers);
	return request
		.then(() => {
			dispatch(ActionsDialog.closeDialog());
			dispatch(getStacks());
			dispatch(ActionsDialog.toggleLoading());
		})
		.catch(err => {
			dispatch(ActionsDialog.toggleLoading());
			dispatch(setStackError(stackError(err, 'form')));
		});
};

export const finalizeBuildingCustomACMStack = (id) => dispatch => {
	dispatch(ActionsDialog.toggleLoading());
	const request = axios.post(`${API_BASE_URL}/api/stacks/${id}/finalize-build`, {}, headers);
	return request
		.then(() => {
			dispatch(ActionsDialog.closeDialog());
			dispatch(getStacks());
			dispatch(ActionsDialog.toggleLoading());
		})
		.catch(err => {
			dispatch(ActionsDialog.toggleLoading());
			dispatch(setStackError(stackError(err, 'modal')));
		});
};
// * ////////////// PUT ////////////// * //

export const editStack = stack => dispatch => {
	const formData = new FormData();

	// A stack whose provisioner/file-upload fields were never touched has no
	// `data.provision` at all (Stack.js only ever creates it lazily, on the
	// first script/file upload) - every access below then throws on
	// `undefined.nodeProvisioner` etc, which aborted this whole function
	// before the PUT request was ever sent, silently no-op'ing the Update
	// button for any such stack. Caught on a real edit 2026-10-20.
	stack.data.provision = stack.data.provision || {};

	//add modeProvision file
	if (stack.data.provision.nodeProvisioner && stack.data.provision.nodeProvisioner.name) {
		formData.append('nodeProvisioner', stack.data.provision.nodeProvisioner, stack.data.provision.nodeProvisioner.name);
	}

	if (stack.data.provision.serverProvisioner && stack.data.provision.serverProvisioner.name) {
		formData.append(
			'serverProvisioner',
			stack.data.provision.serverProvisioner,
			stack.data.provision.serverProvisioner.name
		);
	}

	if (stack.data.provision.unifiedProvisioner && stack.data.provision.unifiedProvisioner.name) {
		formData.append(
			'unifiedProvisioner',
			stack.data.provision.unifiedProvisioner,
			stack.data.provision.unifiedProvisioner.name
		);
	}

	if (stack.data.provision.dataFilesB) {
		stack.data.provision.dataFilesB.forEach(file => {
			formData.append(file.name, file);
			stack.data.provision.dataFiles = stack.data.provision.dataFiles.filter(e => e !== file.name);
		});
	}

	delete stack.data.provision.dataFilesB;
	formData.set('stack', JSON.stringify(stack).replace('{}', '""'));

	dispatch(ActionsDialog.toggleLoading());
	const request = axios.put(`${API_BASE_URL}/api/stacks/`, formData, headers);
	return request
		.then(() => {
			dispatch(ActionsDialog.closeDialog());
			dispatch(getStacks());
			dispatch(ActionsDialog.toggleLoading());
		})
		.catch(err => {
			dispatch(ActionsDialog.toggleLoading());
			dispatch(setStackError(stackError(err, 'form')));
		});
};

export const editCollaborators = data => dispatch => {
	dispatch(ActionsDialog.toggleLoading());
	const request = axios.put(`${API_BASE_URL}/api/stacks/collaborators`, data, headers);
	return request
		.then(res => {
			dispatch(setSelected(res.data.stack));
			dispatch(ActionsDialog.toggleLoading());
			dispatch(getStacks());
		})
		.catch(err => {
			dispatch(ActionsDialog.toggleLoading());
			dispatch(setStackError(stackError(err, 'modal')));
		});
};

// * ////////////// DELETE ////////////// * //

/** One line for the toast: what happened to the AWS image, if deletion was asked for. */
export const describeCloudImageResult = (cloudImage) => {
	if (!cloudImage) return 'Stack Deleted';
	if (cloudImage.error) return `Stack deleted; could not delete the AWS image: ${cloudImage.error}`;
	if (cloudImage.skipped) return `Stack deleted; AWS image kept: ${cloudImage.skipped}`;
	const parts = [];
	(cloudImage.deleted || []).forEach((d) => parts.push(`deregistered ${d.name || d.imageId} and deleted ${d.snapshots} snapshot${d.snapshots === 1 ? '' : 's'}`));
	if ((cloudImage.alreadyGone || []).length) parts.push('the AWS image was already gone');
	(cloudImage.failed || []).forEach((f) => parts.push(`could not delete ${f.name || f.imageId}: ${f.error}`));
	return parts.length ? `Stack deleted; ${parts.join('; ')}` : 'Stack deleted; no AWS image to delete';
};

// No global loading flag here: deregistering an AWS image and its snapshots can
// take close to a minute, and the global flag disables every form in the app
// (New Stack's Save and Advanced included). The row shows its own "Deleting..." state.
export const deleteStack = (id, provider, deleteCloudImage = false) => dispatch => {
	const request = axios.delete(`${API_BASE_URL}/api/stacks/${id}`, {
		headers,
		data: { provider, deleteCloudImage }
	});
	let cloudImage = null;
	return request
		.then((response) => { cloudImage = response && response.data ? response.data.cloudImage : null; return dispatch(getStacksRepeat()); })
		.then(() => {
			dispatch(showMessage({ message: describeCloudImageResult(cloudImage), autoHideDuration: cloudImage ? 10000 : undefined }));
		})
		.catch(err => {
			const errorMessage = errMessage(err, 'An error occurred while deleting the stack');
			dispatch(setStackError({ message: errorMessage, type: 'modal' }));

		});
};

// * ////////////// ACTIONS ////////////// * //

export const buildStack = (stackId, stacksArray) => dispatch => {
	dispatch(ActionsDialog.toggleLoading());

	const payload = { id: stackId, stacks: stacksArray };
	const request = axios.post(`${API_BASE_URL}/api/stacks/build`, payload, headers);

	return request
		.then(() => {
			dispatch(ActionsDialog.closeDialog());
			dispatch(getStacks());
			dispatch(ActionsDialog.toggleLoading());
		})
		.catch(err => {
			dispatch(ActionsDialog.toggleLoading());
			dispatch(setStackError(stackError(err, 'modal')));
		});
};

export const cancelBuild = (stackId) => dispatch => {
	const request = axios.post(`${API_BASE_URL}/api/stacks/${stackId}/cancel-build`, {}, headers);
	return request
		.then(() => {
			dispatch(showMessage({ message: 'Cancelling build...' }));
			dispatch(getStacks());
		})
		.catch(err => {
			const errorMessage = errMessage(err, 'Failed to cancel build');
			dispatch(showMessage({ message: errorMessage }));
		});
};

export const getStackError = (stackId) => dispatch => {
	const request = axios.get(`${API_BASE_URL}/api/stacks/error/${stackId}`, headers);
	
	return request
		.then(response => {
			if (response.data && response.data.error) {
				// Show dialog with error instructions
				const error = response.data.error;
				
				dispatch(ActionsDialog.openDialog({
					maxWidth: 'md',
					fullWidth: true,
					children: <StackErrorDialog error={error} stackId={stackId} />
				}));
			} else {
				dispatch(showMessage({ message: 'No error details available for this stack', variant: 'info' }));
			}
		})
		.catch(err => {
			const errorMessage = errMessage(err, 'Failed to fetch error details');
			dispatch(showMessage({ message: errorMessage, variant: 'error' }));
		});
};

// * ////////////// IMAGE CUSTOMIZATION ////////////// * //

export const getCustomization = stackId => (dispatch, getState) => {
	const request = axios.get(`${API_BASE_URL}/api/stacks/${stackId}/customization`);
	return request
		.then(response => {
			const data = response.data;
			if (data) return dispatch(setCustomization(data));
			// Server reports no active session. Ignore a TRANSIENT null while a session
			// is mid-flight (e.g. the instant after Launch, or mid-capture) so the dialog
			// doesn't snap back to the launch view and stop polling. Only clear when the
			// current session is settled or already gone.
			const cur = getState().stacks && getState().stacks.stack && getState().stacks.stack.customization;
			const transientState = cur && ['deploying', 'preparing', 'starting', 'stopping', 'customizing'].indexOf(cur.state) !== -1;
			const capturing = cur && cur.captureStatus && cur.captureStatus !== 'available' && cur.captureStatus !== 'failed';
			if (transientState || capturing) return undefined;
			return dispatch(setCustomization(null));
		})
		.catch(err => dispatch(showMessage({ message: errMessage(err, 'Failed to load customization session'), variant: 'error' })));
};

export const getShapes = stackId => dispatch => {
	const request = axios.get(`${API_BASE_URL}/api/stacks/${stackId}/shapes`);
	return request
		.then(response => dispatch(setShapes(response.data)))
		.catch(err => dispatch(showMessage({ message: errMessage(err, 'Failed to load instance shapes'), variant: 'error' })));
};

export const launchCustomization = (stackId, opts = {}) => dispatch => {
	// No global loading overlay here: it would cover the dialog. The dialog reflects
	// progress via the session state (deploying -> available) from getCustomization.
	const request = axios.post(`${API_BASE_URL}/api/stacks/${stackId}/customize`, {
		instanceProfile: opts.instanceProfile || undefined,
		volumeSize: opts.volumeSize || undefined,
		displayName: opts.displayName || undefined,
		versionId: opts.versionId || undefined
	});
	return request
		.then(response => {
			dispatch(showMessage({ message: 'Customization session launching...', variant: 'info' }));
			// Optimistically flip the dialog to the live view the instant Launch returns,
			// so the user never has to close/reopen. getCustomization + polling then fill
			// in the real state/steps.
			if (response.data && response.data.clusterId) {
				dispatch(setCustomization({
					id: response.data.clusterId,
					displayName: opts.displayName || null,
					state: 'deploying',
					deployProgress: 0,
					deploySteps: [],
					captureStatus: null,
					desktopEnabled: false
				}));
			}
			return dispatch(getCustomization(stackId)).then(() => response.data);
		})
		.catch(err => {
			dispatch(showMessage({ message: errMessage(err, 'Failed to launch customization session'), variant: 'error' }));
		});
};

export const captureImage = (stackId, displayName, note) => dispatch => {
	const request = axios.post(`${API_BASE_URL}/api/stacks/${stackId}/customization/capture`, { displayName, note });
	return request
		.then(() => {
			dispatch(showMessage({ message: 'Capturing new image. This can take several minutes...', variant: 'info' }));
			return dispatch(getCustomization(stackId));
		})
		.catch(err => dispatch(showMessage({ message: errMessage(err, 'Failed to start image capture'), variant: 'error' })));
};

export const discardCustomization = stackId => dispatch => {
	const request = axios.post(`${API_BASE_URL}/api/stacks/${stackId}/customization/discard`, {});
	return request
		.then(() => {
			dispatch(showMessage({ message: 'Discarding customization session...', variant: 'info' }));
			dispatch(setCustomization(null));
		})
		.catch(err => dispatch(showMessage({ message: errMessage(err, 'Failed to discard session'), variant: 'error' })));
};

export const getVersions = stackId => dispatch => {
	const request = axios.get(`${API_BASE_URL}/api/stacks/${stackId}/versions`);
	return request
		.then(response => dispatch(setVersions(response.data)))
		.catch(err => dispatch(showMessage({ message: errMessage(err, 'Failed to load image versions'), variant: 'error' })));
};

export const setDefaultVersion = (stackId, versionId) => dispatch => {
	const request = axios.put(`${API_BASE_URL}/api/stacks/${stackId}/versions/${versionId}/default`, {});
	return request
		.then(() => {
			dispatch(showMessage({ message: 'Default image updated', variant: 'success' }));
			return dispatch(getVersions(stackId));
		})
		.catch(err => dispatch(showMessage({ message: errMessage(err, 'Failed to set default image'), variant: 'error' })));
};
